import { useState } from 'react';
import { Upload, ShieldCheck } from 'lucide-react';

import { Button } from '../../components/core/Button';
import { Badge } from '../../components/core/Badge';
import { useTranslation } from '../../context/LanguageContext';
import { useMediaLibrary } from '../../hooks/useMediaLibrary';
import { UploadZone } from '../../components/dashboard/media/UploadZone';
import { MediaFilters } from '../../components/dashboard/media/MediaFilters';
import { MediaCard } from '../../components/dashboard/media/MediaCard';
import { MediaViewer } from '../../components/dashboard/media/MediaViewer';
import type { MediaAsset } from '../../hooks/useMediaLibrary';

export function MediaLibraryPage() {
  const { t } = useTranslation();
  const {
    assets,
    totalCount,
    uploading,
    filter,
    setFilter,
    uploadFiles,
    cancelUpload,
    dismissUploadError,
    archive,
    restore,
    remove,
    isSuperAdmin,
  } = useMediaLibrary();

  const [showUpload, setShowUpload] = useState(false);
  const [viewing, setViewing] = useState<MediaAsset | null>(null);

  const handleFiles = async (files: File[]) => {
    setShowUpload(false);
    await uploadFiles(files);
  };

  return (
    <div className="space-y-5 max-w-[1280px]">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-bold text-white">{t('media.title')}</h2>
          {isSuperAdmin && (
            <Badge variant="ai" className="flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" />
              {t('media.adminBadge')}
            </Badge>
          )}
        </div>
        <Button size="sm" onClick={() => setShowUpload((v) => !v)} className="gap-1.5">
          <Upload className="h-3.5 w-3.5" />
          {t('media.upload')}
        </Button>
      </div>

      {/* Upload zone (collapsible) */}
      {(showUpload || uploading.length > 0) && (
        <UploadZone
          uploading={uploading}
          onFiles={(files) => void handleFiles(files)}
          onCancel={cancelUpload}
          onDismissError={dismissUploadError}
        />
      )}

      {/* Filters */}
      <MediaFilters filter={filter} onChange={setFilter} totalCount={totalCount} />

      {/* Grid */}
      {assets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Upload className="h-10 w-10 text-slate-700 mb-4" />
          <p className="text-sm font-semibold text-slate-300">
            {totalCount === 0 ? t('media.empty') : t('media.emptyFilterHint')}
          </p>
          <p className="text-xs text-slate-500 mt-1.5 max-w-xs">
            {totalCount === 0 ? t('media.emptyHint') : ''}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
          {assets.map((asset) => (
            <MediaCard
              key={asset.id}
              asset={asset}
              isAdmin={isSuperAdmin}
              onView={setViewing}
              onArchive={archive}
              onRestore={restore}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      {/* Viewer */}
      <MediaViewer asset={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
